import { useCallback, useEffect, useState } from 'react';

/**
 * Whether the person collapsed the rail to its icons (Brand Kit § The frame: "the collapse
 * control"). The shell reads it as `data-rail` on `.app-shell`, and `shell.css` narrows the rail to
 * 64px from 900px up; everything the narrow rail looks like follows from the rail's own width, so
 * nothing else in the codebase reads this value.
 *
 * A LOCAL preference, stored only in this browser, for the reason `density.ts` gives for density:
 * it is a display choice, and there is no profile column to write it through to.
 *
 * Below 1180px the rail is compressed by width whatever this says, and below 900px it leaves the
 * flow. The stored choice is untouched by either and applies again when the window is wide enough.
 */
export type RailPreference = 'expanded' | 'collapsed';

export const RAIL_STORAGE_KEY = 'dgb-console:rail';

/** Expanded is the default: the rail a first-time user gets names every module. */
export const DEFAULT_RAIL: RailPreference = 'expanded';

export function isRailPreference(value: unknown): value is RailPreference {
  return value === 'expanded' || value === 'collapsed';
}

/** Defensive read, mirroring `readStoredDensity`: blocked storage falls back to the default. */
export function readStoredRail(): RailPreference {
  try {
    const raw = localStorage.getItem(RAIL_STORAGE_KEY);
    return isRailPreference(raw) ? raw : DEFAULT_RAIL;
  } catch {
    return DEFAULT_RAIL;
  }
}

export function writeStoredRail(rail: RailPreference): void {
  try {
    localStorage.setItem(RAIL_STORAGE_KEY, rail);
  } catch {
    // No-op: the choice still applies to this session, it just does not survive a reload.
  }
}

/**
 * The shell's one piece of rail state. Read synchronously on the first render, so a person who
 * collapsed the rail never sees it open and then close on reload.
 */
export function useRailPreference(): { rail: RailPreference; toggleRail: () => void } {
  const [rail, setRail] = useState<RailPreference>(readStoredRail);

  useEffect(() => {
    writeStoredRail(rail);
  }, [rail]);

  const toggleRail = useCallback(() => {
    setRail((current) => (current === 'collapsed' ? 'expanded' : 'collapsed'));
  }, []);

  return { rail, toggleRail };
}
